import { useEffect, useMemo, useRef, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import type { MatchPair } from '../types';

// API response type for valid results endpoint
interface ValidResult {
  model_config_id: string;
  model_config_name: string;
  [key: string]: unknown;
}

// Store loaded data with its match key so we can derive validity
interface LoadedMatchData {
  matchKey: string;
  results: [ValidResult, ValidResult];
}

/** Create a stable key from a match pair */
function getMatchKey(match: MatchPair | null): string | null {
  return match ? `${match[0]}-${match[1]}` : null;
}

/**
 * Hook to fetch and manage results for a specific match pair.
 * Uses derived state pattern - results only show if they match the current match.
 */
export function useMatchResults(currentMatch: MatchPair | null) {
  const [loadedData, setLoadedData] = useState<LoadedMatchData | null>(null);
  // Track the match we're currently loading to handle race conditions
  const loadingMatchRef = useRef<string | null>(null);

  // Derive match key from current match
  const currentMatchKey = getMatchKey(currentMatch);

  // Derive results - only show if loaded data matches current match
  const currentMatchResults = useMemo(() => {
    if (!currentMatchKey || !loadedData) return null;
    if (loadedData.matchKey !== currentMatchKey) return null;
    return loadedData.results;
  }, [currentMatchKey, loadedData]);

  // Derive loading state - loading if we have a match but results don't match yet
  const isLoading = currentMatchKey !== null && currentMatchResults === null;

  // Fetch results when current match changes (useEffect is appropriate for async fetching)
  useEffect(() => {
    if (!currentMatch) {
      loadingMatchRef.current = null;
      return;
    }

    const matchKey = getMatchKey(currentMatch);
    if (!matchKey) return;

    // Track which match we're loading
    loadingMatchRef.current = matchKey;

    // Capture values for the async closure
    const matchToLoad: MatchPair = currentMatch;
    const matchKeyToLoad = matchKey;

    async function loadCurrentMatchResults() {
      try {
        const [resultAId, resultBId] = matchToLoad;

        // Fetch both results in parallel
        const [resultAResponse, resultBResponse] = await Promise.all([
          apiRequest(`/api/arena/results/${resultAId}`),
          apiRequest(`/api/arena/results/${resultBId}`),
        ]);

        const results: [ValidResult, ValidResult] = [
          resultAResponse.result,
          resultBResponse.result,
        ];

        // Only update if this is still the match we want
        if (loadingMatchRef.current === matchKeyToLoad) {
          setLoadedData({ matchKey: matchKeyToLoad, results });
        }
      } catch (error) {
        logError('Error fetching match results', error);
      }
    }

    loadCurrentMatchResults();
  }, [currentMatch]);

  return { currentMatchResults, isLoading };
}
